<script lang="ts">
	/**
	 * The product options this sale was made with, and what each of them took off (§4.8).
	 *
	 * `AcceptedTerms`'s sibling, in `components/` for the same reason it is: support reads it on
	 * `/Settings/Admin/Transactions/*` and the customer reads it on `/Account/Subscription/:uuid`, and two
	 * renderings of one record is a support conversation with nothing left to resolve it.
	 *
	 * It answers a question the terms panel does not, which is why it is a panel of its own rather
	 * than rows added to that one: **what was this customer charged, and why**. A discount taken in
	 * exchange for an obligation is the commonest thing a customer disputes, and the live option
	 * cannot settle it — the percentage is editable, and quoting today's figure under last year's
	 * date would restate the purchase as having been sold on terms nobody offered then. Everything
	 * shown is the snapshot on the selection row; the version badge says whether the two still
	 * agree, which is a comparison rather than a reason to substitute one for the other.
	 *
	 * Each option is collapsed by default, for the reason the terms are: these are wordings, there
	 * are usually several, and open they push the rest of the screen below the fold on every visit.
	 */

	import { Badge } from 'flowbite-svelte';
	import EmptyState from './EmptyState.svelte';
	import Panel from './Panel.svelte';
	import { formatFriendlyDate, formatMoney } from '../Utilities';
	import type { ProductOptionSelection, StorefrontCurrency } from '../AppTypes';

	/**
	 * Undefined and empty are different answers and the copy says so.
	 *
	 * Undefined is a server that predates the field, where "no options were taken" would be a claim
	 * this screen cannot support; empty is a purchase that genuinely took none — which is most of
	 * them, and is not a fault.
	 */
	export let selections: ProductOptionSelection[] | undefined = undefined;
	/**
	 * The currency rows this store knows, so a figure can be formatted from its own
	 * `minorUnitDigits`.
	 *
	 * A list rather than one row, because a selection carries the currency the purchase was billed
	 * in and this panel renders whatever it is handed — which on a support screen is routinely not
	 * the currency the admin's own store defaults to. Where the row is missing the figure is
	 * rendered with the code beside the minor units rather than guessed at: `amount / 100` is
	 * right for USD and a hundred times wrong for JPY, and it is wrong silently.
	 */
	export let currencies: StorefrontCurrency[] = [];

	/**
	 * Whether to show the application key — **off unless a support screen asks for it**.
	 *
	 * The key is a wire value: the string an integration switches on, and the answer to "the
	 * feature is not enabled for this customer". That is a question support arrives with and one a
	 * customer has no use for, so the customer's own page shows the wording and the money and
	 * nothing else — an internal identifier on a page written for the person who bought something
	 * reads as debug output that escaped.
	 *
	 * Default **false** rather than true, so that a screen added later cannot leak it by omission.
	 * Forgetting to pass it costs an admin a debugging aid on a screen that has other ways to get
	 * the key; the opposite default puts internal wiring in front of a customer, which is the
	 * mistake this prop exists to fix.
	 */
	export let showKey: boolean = false;

	/** Which one is open. One at a time, as the terms panel has it. */
	let openUUID: string = '';

	/**
	 * What the option took off, as a sentence, or null where it took nothing.
	 *
	 * Both halves are stated for a percentage — the rule and what the rule came to — because the
	 * rule alone cannot be checked against an invoice and the figure alone cannot be checked
	 * against the catalogue, and support is usually holding one of the two.
	 */
	function savingOf(selection: ProductOptionSelection): string | null {
		// Which side of the invoice this landed on. The two are separate columns rather than one
		// signed number precisely so a reader here cannot mistake one for the other — and "€10 off"
		// rendered where "€10 added" belongs is the worst sentence this panel could produce.
		const charging = selection.chargeAmount !== null || selection.chargePercent !== null;
		const percent = charging ? selection.chargePercent : selection.percentOff;
		const amount = charging ? selection.chargeAmount : selection.discountAmount;
		const direction = charging ? 'added' : 'off';
		const cadence = charging && selection.chargeOnce ? ', once' : '';

		if (amount === null || selection.currency === null) {
			return percent === null ? null : `${percent}% ${direction}${cadence}`;
		}
		const row = currencies.find(entry => entry.code === selection.currency);
		const money =
			row === undefined
				? `${amount} ${(selection.currency ?? '').toUpperCase()}`
				: formatMoney(amount, row);
		return percent === null
			? `${money} ${direction}${cadence}`
			: `${percent}% ${direction} — ${money}${cadence}`;
	}

	/** Green is the money colour for something earned; an extra that reads green reads as free. */
	function isCharge(selection: ProductOptionSelection): boolean {
		return selection.chargeAmount !== null || selection.chargePercent !== null;
	}

	function fnToggle(uuid: string): void {
		openUUID = openUUID === uuid ? '' : uuid;
	}
</script>

<Panel title="Options taken" faIcon="sliders">
	<svelte:fragment slot="titleRight">
		{#if selections !== undefined}
			<span class="text-sm text-default">{selections.length}</span>
		{/if}
	</svelte:fragment>

	{#if selections === undefined}
		<EmptyState
			compact
			title="This server does not report product options."
			description="Nothing was recorded either way on this screen, so check the options directly."
		/>
	{:else if selections.length === 0}
		<EmptyState
			compact
			title="No options were taken on this purchase."
			description="Options are offered per product and per product family on the catalogue screen, and taking one is always the customer's choice."
		/>
	{:else}
		<ul class="space-y-2">
			{#each selections as selection (selection.uuid)}
				<li class="widget bg-depth">
					<!-- A real button, so the keyboard reaches it and the whole row is the target. -->
					<button
						type="button"
						class="flex w-full flex-wrap items-center gap-x-3 gap-y-1 text-left"
						aria-expanded={openUUID === selection.uuid}
						on:click={() => fnToggle(selection.uuid)}
					>
						<i
							class="fa-solid {openUUID === selection.uuid ? 'fa-angle-up' : 'fa-angle-down'} text-default"
						/>
						<span class="font-medium">{selection.title}</span>
						<Badge color="dark">v{selection.version}</Badge>
						{#if savingOf(selection) !== null}
							<!--
								The money, on the header line and outside the collapse, for the
								reason a term's answer is: it is the part somebody came to look up,
								and it is also what tells two selections of the same option apart.
								`tabular-nums` because the table rule opts a money column back out
								of `overflow-wrap: anywhere`, and a figure broken mid-number still
								looks like a figure.
							-->
							<Badge color={isCharge(selection) ? 'dark' : 'green'}>
								<span class="tabular-nums">{savingOf(selection)}</span>
							</Badge>
						{/if}
						{#if selection.isCurrentVersion === false}
							<!--
								Not a fault, and worded so it does not read as one: the customer took
								what they were offered, and the option has been edited since. It is
								here because support otherwise compares this against the catalogue
								screen and finds a difference with no explanation.
							-->
							<Badge color="yellow">Option edited since</Badge>
						{:else if selection.optionUUID === null}
							<Badge color="yellow">Option since removed</Badge>
						{/if}
						<span class="grow" />
						<span class="text-sm text-default">
							Chosen {formatFriendlyDate(selection.selectedAt)}
						</span>
					</button>

					{#if openUUID === selection.uuid}
						<div class="mt-3 space-y-2 border-t border-widget-color pt-3">
							<!--
								`richtext`, because preflight strips paragraph and list styling out
								of any HTML the app did not author — and the wording is what says
								what the customer actually undertook to do.
							-->
							<div class="richtext text-sm">{@html selection.bodyHTML}</div>
							{#if showKey}
								<p class="text-xs text-default">
									<!--
										The key, on a support screen only: it is the string the
										application switches on, and "the feature is not enabled for
										this customer" is a ticket answered by comparing it against
										what the integration reads. It wraps rather than scrolls —
										the popup and table rules in `app.postcss` handle an
										unbreakable token.
									-->
									Application key <span class="font-mono">{selection.key}</span>
								</p>
							{/if}
						</div>
					{/if}
				</li>
			{/each}
		</ul>
	{/if}
</Panel>
